import type { LucideIcon } from "lucide-react";

/**
 * Bubble configuration shape, ported from `_legacy/xaes-fx.js`
 * `BUBBLE_DATA`. Each WS panel + MC_00 has a different set of bubbles;
 * the vanilla code mutated DOM via `_renderBubble` from this same shape.
 * In the React port, a `Bubble` component reads this config and renders
 * its SVG children declaratively.
 */
export type BubbleConfig = {
  /** Stable id — used as React key and as the bubble's DOM id for
   *  cross-cutting concerns (tooltip targets, hit-testing). */
  id: string;

  /** Centre coordinates within the parent SVG viewBox. */
  cx: number;
  cy: number;

  /** Outer (filled) circle radius. */
  outerR: number;
  /** Inner (dashed, spinning ring) circle radius. */
  innerR: number;

  /** Outer circle fill — a `url(#gradId)` reference into the defs. */
  fill: string;
  /** Outer circle stroke — hex or `var(--…)` CSS variable. */
  stroke: string;
  outerStrokeW: number;
  outerOp: number;
  ringStrokeW: number;
  ringOp: number;

  /** SVG filter to apply (e.g. `url(#glow00)`). */
  filter: string;

  /** Optional override X for the icon glyph (defaults to cx - iconSize/2). */
  iconTx?: number;
  /** Icon glyph scale factor (1 = 24px). */
  iconScale: number;

  titleFontSize: number;
  letterSpacing: number;
  /** Half-width of the title/body divider line. */
  dividerHW: number;
  bodyFontSize: number;

  titleLines: string[];
  bodyLines: string[];

  /**
   * Lucide React icon to render inside the bubble.
   *
   * Deliberate departure from `_legacy/xaes-fx.js`, which used
   * Material Symbols Outlined via a Google Font. lucide-react is
   * already in our deps, ships as inline SVG (CSP-clean — no Google
   * Fonts), tree-shakes per icon, and renders at any size without
   * needing a font subset.
   */
  icon?: LucideIcon;

  /** Tooltip title (rendered by an ancestor tooltip system). */
  tipTitle?: string;
  /** Tooltip body. A plain string renders as one paragraph; an array
   *  of strings renders as a bullet list — easier for humans to scan
   *  than a dense paragraph. */
  tipBody?: string | string[];

  /** Optional sub-label beneath the bubble (WS8 phase bubbles). */
  label?: {
    title: string;
    bodyLines: string[];
    color?: string;
  };

  /**
   * Completion fill, 0-1 (MC_00 capability bubbles only). Renders a
   * liquid-style fill rising from the bottom of the outer circle,
   * proportional to how many of the bubble's linked workshops are done —
   * see `BUBBLE_WORKSHOPS` in `useBubbleWorkshopLink.ts`. `undefined`
   * renders no fill at all (bubbles with no linked workshops).
   */
  fillPercent?: number;
};

/**
 * Global tuning values for the bubble renderer.
 * Ported from `window.BUBBLE` in `_legacy/xaes-fx.js`.
 */
export type BubbleLayout = {
  /** CSS animation duration for the spinning dashed ring. */
  ringSpeed: string;
  /** Equal visual gap: icon→title, title→divider, divider→body (px). */
  spacing: number;
  /** Line-height for multi-line titles (px). */
  titleLineH: number;
  /** Line-height for body text (px). */
  bodyLineH: number;
  /** Optional WS8-style sub-label under a bubble (SVG user units). */
  labelFontSize?: number;
};

export const DEFAULT_BUBBLE_LAYOUT: BubbleLayout = {
  ringSpeed: "110s",
  spacing: 7,
  titleLineH: 12,
  bodyLineH: 12,
  labelFontSize: 9,
};

/** Tighter, larger type for the workshop-dashboard theme diagram slot. */
export const DASHBOARD_BUBBLE_LAYOUT: BubbleLayout = {
  ringSpeed: "110s",
  spacing: 6,
  titleLineH: 16,
  bodyLineH: 15,
  labelFontSize: 12,
};
